import { Helmet } from 'react-helmet'
import { Link, useNavigate } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableSkeletonRows } from '@/components/ui/table-skeleton'
import { formatDateTime } from '@/lib/formatters'
import { type Sequence, SequenceStepStatus, useActiveSequences } from '@/models/sequence'
import { summarize } from '../SalesLeads/Sequence/SequenceStepCard'
import { leadSequencesPath, progress, sequencePath, STATUS_VARIANT } from './sequenceStatus'

function nextStep(sequence: Sequence): string {
  const pending = sequence.steps.find((s) => s.status === SequenceStepStatus.PENDING)
  return pending ? summarize(pending) : '—'
}

export function SequencesIndex() {
  const { data: sequences = [], isLoading } = useActiveSequences()
  const navigate = useNavigate()

  return (
    <>
      <Helmet>
        <title>Sequences</title>
      </Helmet>
      <div className="flex flex-col gap-6 p-6">
        <h1 className="text-2xl font-semibold">Sequences</h1>

        <Card>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Next step</TableHead>
                  <TableHead>Start</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableSkeletonRows columns={5} />}
                {!isLoading && sequences.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground py-6 text-center">
                      No active sequences.
                    </TableCell>
                  </TableRow>
                )}
                {sequences.map((seq) => (
                  <TableRow
                    key={seq.id}
                    className="cursor-pointer"
                    onClick={() => void navigate(sequencePath(seq.id))}
                  >
                    <TableCell>
                      {seq.salesLead ? (
                        <Link
                          to={leadSequencesPath(seq.salesLead)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm text-blue-600 underline-offset-2 hover:underline"
                        >
                          {seq.salesLeadName}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground text-sm">No lead</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[seq.status]}>{seq.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{progress(seq)}</TableCell>
                    <TableCell className="max-w-xs truncate text-sm">{nextStep(seq)}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatDateTime(seq.dateExecuteStart)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
